import {
  getGetFrontRichiesteIdGestoreQueryKey,
  getGetFrontRichiesteIdProcuratoreQueryKey,
  usePostFrontRichiesteIdGestore,
  usePostFrontRichiesteIdProcuratore,
} from '@framework/api-client';
import { useCurrentUser } from '@framework/auth';
import { modalConfirm } from '@framework/engine';
import { isDelegationCodeEqualToCurrentUser } from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import { useMemo } from 'react';
import { queryClient } from '../../../../core/network/queryClient';
import { ChangeSubmissionManagerValues } from '../components/ChangeSubmissionManagerButton';

/**
 * Hook che gestisce il cambio di Procuratore/Gestore chiamando l'API dedicata
 */
export function useChangeSubmissionManager(type: 'manager' | 'attorney') {
  const { t } = useTranslation();
  const updateManager = usePostFrontRichiesteIdGestore();
  const updateAttorney = usePostFrontRichiesteIdProcuratore({
    request: {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  });

  const currentUser = useCurrentUser();

  const update = useMemo(
    () => ({
      mutateAsync: async (
        submissionId: string,
        values: ChangeSubmissionManagerValues,
      ) => {
        if (type === 'manager') {
          // Se l'utente sta rimuovendo se stesso come gestore
          // chiediamo una conferma
          if (
            !isDelegationCodeEqualToCurrentUser(values.fiscalCode, currentUser)
          ) {
            const isConfirmed = await modalConfirm({
              width: 500,
              title: t(
                'change-submission-manager.conferma-cambio-gestore',
                `Conferma cambio gestore`,
              ),
              content: t("change-submission-manager.sicuro-di-voler-sostituire-il-gestore", `Sei sicuro di voler sostituire il gestore della richiesta? Una volta completata l'operazione potresti perdere la possibilità di accedere alla richiesta.`), // prettier-ignore
            });

            if (!isConfirmed) return false;
          }

          await updateManager.mutateAsync({
            id: submissionId,
            data: {
              codiceFiscale: values.fiscalCode,
            },
          });
          await queryClient.invalidateQueries(
            getGetFrontRichiesteIdGestoreQueryKey(submissionId),
          );
        } else {
          await updateAttorney.mutateAsync({
            id: submissionId,
            data: {
              codiceFiscale: values.fiscalCode,
              file: values.file as Blob,
              permetteInoltro: values.canSubmit ?? false,
            },
          });
          await queryClient.invalidateQueries(
            getGetFrontRichiesteIdProcuratoreQueryKey(submissionId),
          );
        }
        return true;
      },
    }),
    [currentUser, type, updateAttorney, updateManager],
  );

  return { update };
}
